/**
 * Save the life map as a file.
 *
 * The diagram is one `<svg>` with every colour already resolved to a literal
 * (see palette.ts), so exporting is mostly a matter of framing the whole scene
 * instead of the current viewport and painting a background in — an SVG has no
 * page colour of its own, and a map saved from the dark theme would otherwise
 * open as dark text on white.
 */

interface ExportOptions {
  /** The whole scene, not what happens to be on screen. */
  viewBox: { x: number; y: number; width: number; height: number }
  background: string
  filename: string
}

function standaloneMarkup(svg: SVGSVGElement, opts: ExportOptions): string {
  const clone = svg.cloneNode(true) as SVGSVGElement
  const pad = 28
  const vb = opts.viewBox

  clone.setAttribute(
    'viewBox',
    `${vb.x - pad} ${vb.y - pad} ${vb.width + pad * 2} ${vb.height + pad * 2}`,
  )
  clone.setAttribute('width', String(Math.round(vb.width + pad * 2)))
  clone.setAttribute('height', String(Math.round(vb.height + pad * 2)))
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink')
  clone.removeAttribute('style')
  clone.removeAttribute('class')

  // Anything that only makes sense while pointing at it.
  clone.querySelectorAll('[data-export="omit"]').forEach((el) => el.remove())

  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
  bg.setAttribute('x', String(vb.x - pad))
  bg.setAttribute('y', String(vb.y - pad))
  bg.setAttribute('width', String(vb.width + pad * 2))
  bg.setAttribute('height', String(vb.height + pad * 2))
  bg.setAttribute('fill', opts.background)
  clone.insertBefore(bg, clone.firstChild)

  return `<?xml version="1.0" encoding="UTF-8"?>\n${new XMLSerializer().serializeToString(clone)}`
}

function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoking immediately can cancel the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}

export function downloadSvg(svg: SVGSVGElement, opts: ExportOptions) {
  const blob = new Blob([standaloneMarkup(svg, opts)], {
    type: 'image/svg+xml;charset=utf-8',
  })
  download(blob, `${opts.filename}.svg`)
}

/** A 2× raster, for places that will not take an SVG (chat, slides, print). */
export async function downloadPng(
  svg: SVGSVGElement,
  opts: ExportOptions,
): Promise<void> {
  const markup = standaloneMarkup(svg, opts)
  const scale = 2
  const pad = 28
  const w = Math.round((opts.viewBox.width + pad * 2) * scale)
  const h = Math.round((opts.viewBox.height + pad * 2) * scale)

  const url = URL.createObjectURL(
    new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }),
  )
  try {
    const img = new Image()
    img.decoding = 'sync'
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('the diagram could not be rasterised'))
      img.src = url
    })

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('the diagram could not be rasterised')
    ctx.drawImage(img, 0, 0, w, h)

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/png'),
    )
    if (!blob) throw new Error('the diagram could not be rasterised')
    download(blob, `${opts.filename}.png`)
  } finally {
    URL.revokeObjectURL(url)
  }
}
